<template>
	<transition name="fade">
		<div class="zw-payment-collection" v-if="showUpcoming">
			<div class="ui zw-payment segment" :class="{ 'loading' : showLoader }" @click="loadPaymentInfo()">
				<div class="status">
					<!-- Logic to set status -->
					<a class="ui yellow empty circular label"/>
				</div>

				<div class="date">
					<div class="ui mini statistic">
						<div class="value">
							<!-- Logic to set day -->
							{{ getSubscriptionDate }}
						</div>
						<div class="label">
							<!-- Logic to set month -->
							{{ getSubscriptionMonth }}
						</div>
						<div class="label">
							{{ getSubscriptionYear }}
						</div>
					</div>
				</div>

				<div class="merchant">
					<h3 class="ui header">
						<!-- Logic to get the subscription -->
						{{ subscription.product.name }}
            <template v-if="subscription.status === 'delayed'">
              (<span class="delayed">{{ $t('globals.status.delayed') }}</span>)
            </template>
						<div class="sub header">
							<!-- Logic to get payment status -->
							{{ $t('components.transaction.paymentstatus.upcoming') }}
						</div>
					</h3>
				</div>

				<div class="amount">
					<h3 class="ui header">
						<template v-if="subscription.pricing_is_pending_meter">
							{{ formatPrice(subscription.pricing_amount, 2) }} / m<sup>3</sup>
							<div class="sub header" style="font-size: 0.8em; color: #999; margin-top: 0.5em;">
								{{ $t('components.transaction.info.pending_meter_notice') || 'Final price based on water usage' }}
							</div>
						</template>
						<template v-else>
							{{ formatPrice(displayPricingAmount, 2) }}
						</template>
						<div class="sub header">
							{{ getCurrency }}
						</div>
						<div v-if="waterPeriodPrice && waterPeriodPrice.period" class="sub header">
							{{ waterPeriodPrice.period.start_date }} — {{ waterPeriodPrice.period.end_date }}
						</div>
					</h3>
				</div>
			</div>
			<div class="ui padded segment zw-payment-info" v-if="showPaymentInfo">
				<div class="ui two column stackable grid">
					<div class="column">
						<h3 class="ui header">
							{{ $t('components.transaction.info.paymentinfo') }}
						</h3>
						<div class="content">
							<div class="list">
								<div class="item">{{ $t('components.transaction.info.to_be_paid', { date: getDateString }) }}</div>
								<div class="item">
									<!-- VISA {{ $t('components.transaction.info.endingin') }} *7589 (Not sure about this one) -->
								</div>
							</div>
						</div>

						<div class="ui header" v-if="selectedAccessories.length > 0">
							{{ $t('components.transaction.info.accessories') }}
						</div>
						<div class="content" v-if="selectedAccessories.length > 0">
							<div class="ui green label" v-for="accessory in selectedAccessories" :key="accessory.id">{{ accessory.name }}</div>
						</div>
					</div>

					<div class="column">
						<h3 class="ui header">
							{{ $t('components.transaction.info.merchantinfo') }}
						</h3>

						<div class="content">
							<div class="ui list">
								<div class="item">{{ activeMerchant.name }}</div>
								<div class="item"><a :href="'telto:' + activeMerchant.phone">{{ activeMerchant.phone }}</a></div>
								<div class="item"><a :href="'mailto:' + activeMerchant.email">{{ activeMerchant.email }}</a></div>
							</div>
						</div>

						<h3 class="ui header">
							{{ $t('components.transaction.info.purchase') }}
						</h3>

						<div class="content">
              <template v-if="subscription.pricing_is_pending_meter">
                <!-- When meter is pending, show simplified view -->
                <div class="ui two column grid">
                  <div class="left aligned column">
                    {{ $t('components.transaction.info.water_rate') || 'Water Rate (per m³)' }}
                  </div>
                  <div class="right aligned column">
                    {{ formatPrice(subscription.pricing_amount, 2) }} {{ getCurrency }}
                  </div>
                </div>
                <div class="ui two column grid">
                  <div class="left aligned column" style="color: #999; font-size: 0.85em;">
                    {{ $t('components.transaction.info.pending_meter_notice') || 'This is the rate per cubic meter. Final charge will be based on water usage.' }}
                  </div>
                </div>
                <div class="ui two column grid" v-if="selectedAccessories.length > 0">
                  <div class="left aligned column" style="color: #999; font-size: 0.85em;">
                    {{ $t('components.transaction.info.accessories_included') || 'Accessories included in above rate' }}
                  </div>
                </div>
              </template>
	              <template v-else-if="waterPeriodPrice">
	                <div v-for="(item, index) in waterPeriodItems" :key="index">
	                  <div class="ui two column grid">
	                    <div class="left aligned column">
	                      {{ item.name }}
	                    </div>
	                    <div class="right aligned column">
	                      {{ getWaterPeriodItemNetPrice(item) }} {{ getCurrency }}
	                    </div>
	                  </div>
	                  <div class="ui two column grid" style="margin-top: -2rem;" v-if="item.vat">
	                    <div class="left aligned column">
	                      25,00% moms:
	                    </div>
	                    <div class="right aligned column">
	                      {{ getWaterPeriodItemVat(item) }} {{ getCurrency }}
	                    </div>
	                  </div>
	                </div>
	              </template>
	              <template v-else>
                <!-- Normal breakdown when meter data is available -->
                <div class="ui two column grid">
                  <div class="left aligned column">
                    {{ subscription.product.name }}
                  </div>
                  <div class="right aligned column">
                    {{ getProductPriceFromTotalPrice(subscription, subscription.pricing_amount) }}
                    {{ getCurrency }}
                  </div>
                </div>
                <div class="ui two column grid" style="margin-top: -2rem;" v-if="subscription.product.apply_vat">
                  <div class="left aligned column">
                    25,00% moms:
                  </div>
                  <div class="right aligned column">
                    {{ getProductPriceVatFromTotalPrice(subscription, subscription.pricing_amount) }} {{ getCurrency }}
                  </div>
                </div>
                <template v-if="selectedAccessories.length > 0">
                  <div v-for="accessory in selectedAccessories" :key="accessory.id">
                    <div class="ui two column grid">
                      <div class="left aligned column">
                        {{ accessory.name }}
                      </div>
                      <div class="right aligned column">
                        {{ getAccessoryPrice(accessory) }} {{ getCurrency }}
                      </div>
                    </div>
                    <div class="ui two column grid" style="margin-top: -2rem;" v-if="accessory.apply_vat">
                      <div class="left aligned column">
                        25,00% moms:
                      </div>
                      <div class="right aligned column">
                        {{ getAccessoryPriceVat(accessory) }} {{ getCurrency }}
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <div class="ui two column grid" v-if="subscription.expire_date">
                <div class="left aligned column" style="color:green">
                  {{ $t('components.transaction.info.expire_date') }}
                </div>
                <div class="right aligned column" style="color:green">
                  {{ getExpireDate }}
                </div>
              </div>
							<div class="ui two column grid">
								<div class="two wide left aligned column"></div>
								<div class="fourteen wide column">
									<table class="ui very basic borderless compact table" v-if="false">
										<tbody>
											<tr v-for="accessory in selectedAccessories" :key="accessory.id">
												<td class="left aligned">{{ accessory.name }}</td>
												<td class="right aligned">{{ formatPrice(accessory.pricing.price) }} {{accessory.pricing.currency}}</td>
											</tr>
											<tr>
												<td class="left aligned">{{ subscription.product.name }}</td>
												<td class="right aligned">{{ formatPrice(subscription.pricing.price) }} {{ getCurrency }}</td>
											</tr>
										</tbody>
									</table>

									<div class="ui divider"></div>

									<table class="ui very basic borderless compact table">
										<tbody>
                    <tr v-if="hasKamstrupMeterUsage">
                      <td class="left aligned">Vandforbrug</td>
                      <td class="right aligned">{{ kamstrupMeterReadingTotal }} m<sup>3</sup></td>
                    </tr>
										<tr>
											<td class="left aligned"><b>{{ $t('components.transaction.info.total') }}</b></td>
											<td class="right aligned">{{ formatPrice(displayPricingAmount) }} {{ getCurrency }}</td>
										</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import find from 'lodash/find'
import moment from 'moment'
import accounting from 'accounting'

export default {
	name: 'UpcomingTransaction',

	props: [ 'subscriptionid' ],

	data () {
		return {
			subscription: null,
			showUpcoming: false,
			showPaymentInfo: false,
			showLoader: false,
			selectedAccessories: []
		}
	},

	mounted () {
		this.fetchSubscription()
			.then(() => {
				this.activateUpcoming()
			})
	},

	watch: {
	  subscriptionid: {
	    immediate: true,
      handler() {
        this.fetchSubscription()
          .then(() => {
            this.activateUpcoming()
          });
      }
    },
		subscription: {
		  immediate: true,
      handler(newSub) {
        if (!this.subscription || this.subscription.id !== newSub.id) {
          this.fetchSubscription()
            .then(() => {
              this.activateUpcoming()
            })
        }
      }
    }
	},

	methods: {
		fetchSubscription () {
			return new Promise((resolve, reject) => {
				this.axios.get(`subscriptions/${this.subscriptionid}`)
				.then(({data}) => {
					this.subscription = data
					resolve(data)
				}).catch(error => {
					reject(error)
				})
			})
		},

		fetchSelectedAccessories () {
			return new Promise((resolve, reject) => {
				const _this = this
				this.selectedAccessories = []

				$.map(JSON.parse(this.subscription.accessories), function (amount, accessoryId) {
					_this.selectedAccessories.push(find(_this.subscription.product.accessories, {id: parseInt(accessoryId)}))
				})

				resolve(this.selectedAccessories)
			})
		},

		loadPaymentInfo () {
			if (!this.showPaymentInfo) {
				this.showLoader = true
				this.fetchSelectedAccessories()
					.then(accessories => {
						this.showPaymentInfo = true
						this.showLoader = false
					})
			} else {
				this.showPaymentInfo = false
			}
		},

		activateUpcoming () {
      if (this.subscription.is_free || this.subscription.final_water_period_payment) {
        this.showUpcoming = false
      } else {
        this.showUpcoming = this.subscription.status === 'active' || this.subscription.status === 'delayed'
      }
		},

		formatPrice (amount, minfrac = 2) {
			if (amount) {
				return accounting.format(amount, minfrac)
			} else {
				const amount = 0
				return accounting.format(amount)
			}
		},

		getWaterPeriodItemNetPrice (item) {
			const grossAmount = parseFloat(item.gross_amount)

			return this.formatPrice(item.vat ? grossAmount * 0.8 : grossAmount)
		},

		getWaterPeriodItemVat (item) {
			const grossAmount = parseFloat(item.gross_amount)

			return this.formatPrice(grossAmount - (grossAmount * 0.8))
		},

    getProductPriceFromTotalPrice (subscription, amount) {
      let meterReadingTotal = this.kamstrupMeterReadingTotal

      if (meterReadingTotal > 0) {
        amount = amount / meterReadingTotal
      }

      if (subscription.accessories === 'null') {
        if (subscription.product.apply_vat) {
          return this.formatPrice(amount * 0.8)
        } else {
          return this.formatPrice(amount)
        }
      } else {
        let totalAccessoriesPrice = 0
        this.selectedAccessories.forEach((value, index) => {
          let accessoryPricing = value.pricing
          if (accessoryPricing.length > 0) {
            accessoryPricing.forEach((value, index) => {
              if (value.interval === subscription.pricing.interval) {
                totalAccessoriesPrice += value.price
              }
            })
          }
        })
        let price = amount - totalAccessoriesPrice
        if (subscription.product.apply_vat) {
          return this.formatPrice(price * 0.8)
        } else {
          return this.formatPrice(price)
        }
      }
    },

    getProductPriceVatFromTotalPrice (subscription, amount) {
      let meterReadingTotal = this.kamstrupMeterReadingTotal

      if (meterReadingTotal > 0) {
        amount = amount / meterReadingTotal
      }

      if (subscription.accessories === 'null') {
        return this.formatPrice(amount - (amount * 0.8))
      } else {
        let totalAccessoriesPrice = 0
        this.selectedAccessories.forEach((value, index) => {
          let accessoryPricing = value.pricing
          if (accessoryPricing.length > 0) {
            accessoryPricing.forEach((value, index) => {
              if (value.interval === subscription.pricing.interval) {
                totalAccessoriesPrice += value.price
              }
            })
          }
        })
        let price = amount - totalAccessoriesPrice
        let vatPrice = price - (price * 0.8)
        return this.formatPrice(vatPrice)
      }
    },

    getAccessoryPrice (accessory) {
      let accessoryPricing = accessory.pricing
      let isVatApplied = accessory.apply_vat
      if (accessoryPricing.length > 0) {
        let accessoryPrice = 0
        accessoryPricing.forEach((value, index) => {
          if (value.interval === this.subscription.pricing.interval) {
            accessoryPrice += value.price
          }
        })
        if (isVatApplied) {
          return this.formatPrice(accessoryPrice * 0.8)
        } else {
          return this.formatPrice(accessoryPrice)
        }
      }
      return 0
    },

    getAccessoryPriceVat (accessory) {
      let accessoryPricing = accessory.pricing
      if (accessoryPricing.length > 0) {
        let accessoryPrice = 0
        accessoryPricing.forEach((value, index) => {
          if (value.interval === this.subscription.pricing.interval) {
            accessoryPrice += value.price
          }
        })
        return this.formatPrice(accessoryPrice - (accessoryPrice * 0.8))
      }
      return 0
    }
	},

	computed: {
		waterPeriodPrice () {
			return this.subscription && this.subscription.water_period_price
				? this.subscription.water_period_price
				: null
		},

		waterPeriodItems () {
			return this.waterPeriodPrice && this.waterPeriodPrice.items
				? this.waterPeriodPrice.items
				: []
		},

		displayPricingAmount () {
			return this.waterPeriodPrice
				? this.waterPeriodPrice.amount
				: this.subscription.pricing_amount
		},

    getDateString () {
      moment.locale(window.localStorage.language || navigator.language || 'da')
      return moment(this.subscription.transaction_date).format('Do MMM, YYYY')
    },

    getSubscriptionDate () {
      return moment(this.subscription.transaction_date).date()
    },

    getExpireDate () {
      moment.locale(window.localStorage.language || navigator.language || 'da')
      return moment(this.subscription.expire_date).format('Do MMM, YYYY');
    },

    getSubscriptionMonth () {
      var months = this.$t('globals.monthsshort')
      return months[moment(this.subscription.transaction_date).month()]
    },

    getCurrency () {
      if (this.subscription.pricing !== null && this.subscription.pricing.currency !== undefined && this.subscription.pricing.currency !== null) {
        return this.subscription.pricing.currency
      } else {
        return 'DKK'
      }
    },

    getSubscriptionYear () {
      return moment(this.subscription.transaction_date).year()
    },

    activeMerchant () {
      return this.$store.getters.activeMerchant
    },

    kamstrupMeterReading () {
      if (
        this.subscription &&
        this.subscription.kamstrup_meters &&
        this.subscription.kamstrup_meters.length > 0
      ) {
        return this.subscription.kamstrup_meters[0]
      }

      return null
    },

    kamstrupMeterReadingTotal () {
      if (!this.kamstrupMeterReading || this.kamstrupMeterReading.meter_reading_total === null) {
        return 0
      }

      return parseFloat(this.kamstrupMeterReading.meter_reading_total)
    },

    hasKamstrupMeterUsage () {
      return this.kamstrupMeterReadingTotal > 0
    }
	}
}
</script>



// WEBPACK FOOTER //
// src/components/payment/UpcomingTransaction.vue